import { confirmSession, createSession } from '@/api/sender';
import { resolveSenderActionError } from '@/lib/resolve-sender-action-error';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import type { SenderPhoneLoginStep } from '@/pages/sender/launch-types';

export type SenderSessionLoginConfirmedMeta = {
	phone: string;
	label: string;
};

export function useSenderSessionLogin(
	onConfirmed: (sessionId: string, meta: SenderSessionLoginConfirmedMeta) => void,
) {
	const [phoneLoginStep, setPhoneLoginStep] = useState<SenderPhoneLoginStep>('phone');
	const [phone, setPhone] = useState('');
	const [label, setLabel] = useState('');
	const [code, setCode] = useState('');
	const [pendingSessionId, setPendingSessionId] = useState<string | null>(null);
	const [codeType, setCodeType] = useState<string>('sms');
	const [isSaving, setIsSaving] = useState(false);

	const resetPhoneLogin = useCallback(() => {
		setPhoneLoginStep('phone');
		setPhone('');
		setLabel('');
		setCode('');
		setPendingSessionId(null);
		setCodeType('sms');
	}, []);

	const requestCode = useCallback(async () => {
		const phoneText = phone.trim();
		if (!phoneText) {
			toast.error('Укажите номер телефона');
			return false;
		}

		setIsSaving(true);
		try {
			const created = await createSession({
				phone: phoneText,
				label: label.trim() || undefined,
			});
			setPendingSessionId(created.id);
			setCodeType(created.codeType);
			setPhoneLoginStep('code');
			toast.success('Код отправлен');
			return true;
		} catch (error) {
			toast.error(resolveSenderActionError(error, 'Не удалось запросить код'));
			return false;
		} finally {
			setIsSaving(false);
		}
	}, [label, phone]);

	const confirmCode = useCallback(async () => {
		if (!pendingSessionId) return false;
		const codeText = code.trim();
		if (!codeText) {
			toast.error('Введите код');
			return false;
		}

		setIsSaving(true);
		try {
			await confirmSession(pendingSessionId, codeText);
			const meta: SenderSessionLoginConfirmedMeta = {
				phone: phone.trim(),
				label: label.trim(),
			};
			toast.success('Сессия готова — настройте рассылку');
			onConfirmed(pendingSessionId, meta);
			resetPhoneLogin();
			return true;
		} catch (error) {
			toast.error(resolveSenderActionError(error, 'Не удалось подтвердить код'));
			return false;
		} finally {
			setIsSaving(false);
		}
	}, [code, label, onConfirmed, pendingSessionId, phone, resetPhoneLogin]);

	const isPhoneLoginInProgress = phoneLoginStep === 'code' && pendingSessionId !== null;

	return {
		phoneLoginStep,
		setPhoneLoginStep,
		phone,
		setPhone,
		label,
		setLabel,
		code,
		setCode,
		codeType,
		isSaving,
		requestCode,
		confirmCode,
		resetPhoneLogin,
		isPhoneLoginInProgress,
	};
}
